import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import type { AccountInfo } from '@azure/msal-browser';
import {
  DEV_USER,
  isDevAuthBypass,
  rolesFromClaims,
} from './index';
import type { ShippingRole, ShippingUser } from '@/types/index';

/**
 * RoleContext — exposes the signed-in user and their App Roles to the tree.
 * Per Shipping-Module-Architecture-v1.md §4.2: authorization is role-based from
 * day one, reading `claims.roles` (Warehouse / Admin).
 *
 * Source of the roles, by environment:
 *  - Dev bypass: DEV_USER (both roles) so every surface is reachable locally.
 *  - Prod: the MSAL active account's id-token claims. The `roles[]` claim is
 *    populated by Entra once the App Roles are defined on the app registration
 *    and the user is assigned via sg-shipping-warehouse / sg-shipping-admin.
 *
 * NOTE (one-line swap, kept honest): the canonical server projection of identity
 * is /api/auth/me (architecture §4.4, modeled on the Reports kit). When the real
 * Azure Functions API exists, replace `userFromMsal()` below with a fetch of
 * /api/auth/me. Until then, reading the id-token claims client-side lets the
 * Warehouse-vs-Admin gating be exercised against a live Entra app with no API.
 */
interface RoleContextValue {
  user: ShippingUser | null;
  roles: ShippingRole[];
  /** True when the user holds the given role (or any of several). */
  hasRole: (...required: ShippingRole[]) => boolean;
  isWarehouse: boolean;
  isAdmin: boolean;
}

const RoleContext = createContext<RoleContextValue | null>(null);

/** Build a ShippingUser from the MSAL active account's id-token claims. */
function userFromMsal(account: AccountInfo | undefined): ShippingUser | null {
  if (!account) return null;
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  return {
    userId: typeof claims['oid'] === 'string' ? (claims['oid'] as string) : account.localAccountId,
    email: account.username,
    displayName: account.name ?? account.username,
    roles: rolesFromClaims(claims),
  };
}

export function RoleProvider({ children }: { children: ReactNode }) {
  const { accounts } = useMsal();

  const value = useMemo<RoleContextValue>(() => {
    const user = isDevAuthBypass() ? DEV_USER : userFromMsal(accounts[0]);
    const roles = user?.roles ?? [];
    const roleSet = new Set(roles);
    return {
      user,
      roles,
      hasRole: (...required: ShippingRole[]) => required.some((r) => roleSet.has(r)),
      isWarehouse: roleSet.has('Warehouse'),
      isAdmin: roleSet.has('Admin'),
    };
  }, [accounts]);

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

/** Read the role context. Throws if used outside <RoleProvider>. */
export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within <RoleProvider>');
  return ctx;
}

/**
 * RequireRole — route/element gate. Renders children only when the user holds at
 * least one of the required App Roles; otherwise redirects to /forbidden.
 *
 * Per architecture §4.1/§4.2: Admin gates config + reports; Warehouse gates the
 * board + build action. Admin is NOT implicitly Warehouse — assign both roles to
 * a user who needs both (DEV_USER does, for local convenience). This mirrors the
 * server-side `requireRole` choke-point the Functions API will enforce, so the UI
 * never shows a control the API would reject.
 */
export function RequireRole({
  anyOf,
  children,
}: {
  anyOf: ShippingRole[];
  children: ReactNode;
}) {
  const { hasRole } = useRole();
  const location = useLocation();
  if (!hasRole(...anyOf)) {
    return <Navigate to="/forbidden" state={{ from: location, needed: anyOf }} replace />;
  }
  return <>{children}</>;
}
